import { useState, useEffect } from 'react';
import { ajaxRequest } from 'app/utils/helpers';
import {
  dlTrackProductIncrease,
  dlTrackAddToBagError,
  dlAddToCart,
  getListPropsForAddToBagAndAddToWishlist,
} from 'app/analytics/addToBagAnalytics';
import { dlAddToWishlist, dlAddToWishlistNew } from 'app/analytics/wishlistAnalytics';
import { MINI_CART_ROOT_ELEMENT, TRANSFER_MINI_CART_ADD } from 'app/header/miniCart/miniCartConstants';
import { MINI_WISHLIST_ROOT_ELEMENT, TRANSFER_MINI_WISHLIST_ADD } from 'app/header/miniWishlist/miniWishlistConstants';
import { STATUS_CODES, RESTORE_ATB_SECONDS } from './addProductConstants';

const { addToBagUrl, addToWishlistUrl } = window.inlineGlobalConfig;
const { miniCartUrl } = window.inlineCartConfiguration;

function useAddProduct({ selectedSize, hasStock, disableNotifyMe }) {
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [status, setStatus] = useState(STATUS_CODES.ADD_PRODUCT_SELECT_SIZE);

  useEffect(() => {
    let timeoutId;
    if (status === STATUS_CODES.ADDED_PRODUCT) {
      timeoutId = setTimeout(() => {
        setStatus(STATUS_CODES.ADD_PRODUCT_READY);
        setIsButtonClicked(false);
      }, RESTORE_ATB_SECONDS);
    }
    return () => clearTimeout(timeoutId);
  }, [status]);

  useEffect(() => {
    const selectedOutOfStock = selectedSize && selectedSize.count <= 0;

    if (!hasStock) {
      setStatus(!disableNotifyMe ? STATUS_CODES.OUT_OF_STOCK : STATUS_CODES.OUT_OF_STOCK_NON_FULL_PRICE);
    }
    if (selectedSize) {
      setStatus(
        (selectedOutOfStock &&
          (!disableNotifyMe ? STATUS_CODES.SIZE_OUT_OF_STOCK : STATUS_CODES.SIZE_OUT_OF_STOCK_NON_FULL_PRICE)) ||
          STATUS_CODES.ADD_PRODUCT_READY
      );
    } else if (hasStock) {
      setStatus(STATUS_CODES.ADD_PRODUCT_SELECT_SIZE);
    }
  }, [hasStock, selectedSize, disableNotifyMe]);

  return { isButtonClicked, setIsButtonClicked, status, setStatus };
}

export function useAddToBag({ selectedSize, hasStock, product, location, disableNotifyMe }) {
  const { isButtonClicked, setIsButtonClicked, status, setStatus } = useAddProduct({
    selectedSize,
    hasStock,
    disableNotifyMe,
  });

  function addToBag() {
    const crossSellUrlParameter = new URL(window.location).searchParams.get('crossSell');
    const crossSell = crossSellUrlParameter ? decodeURIComponent(crossSellUrlParameter) : '';

    let successFlag = '';

    setIsButtonClicked(true);
    if (status === STATUS_CODES.ADD_PRODUCT_READY) {
      setStatus(STATUS_CODES.ADDING_PRODUCT);

      return ajaxRequest('POST', addToBagUrl, {
        sku: selectedSize.sku,
        quantity: 1,
        crossSell,
      })
        .then(res => {
          successFlag = res.statusCode;
          if (res.statusCode === 'success') {
            setStatus(STATUS_CODES.ADDED_PRODUCT);
          } else {
            throw res;
          }
        })
        .then(() => ajaxRequest('GET', `${miniCartUrl}/get`, {}, null, true))
        .then(json => {
          dlTrackProductIncrease({
            product,
            location,
            selectedSize: selectedSize.dataLayerName,
            cart: json,
            successFlag,
          });
          dlAddToCart(
            {
              ...product,
              code: selectedSize.sku,
              selectedSize: selectedSize.dataLayerName,
            },
            getListPropsForAddToBagAndAddToWishlist(product.resultPosition)
          );
          MINI_CART_ROOT_ELEMENT.dispatchEvent(new CustomEvent(TRANSFER_MINI_CART_ADD, { detail: json }));
          return json;
        })
        .catch(res => {
          setStatus(STATUS_CODES.ADD_PRODUCT_ERROR);
          dlTrackAddToBagError({
            product,
            selectedSize: selectedSize.dataLayerName,
          });
          return Promise.reject(res);
        });
    }
    return Promise.resolve();
  }

  return {
    addToBagState: status,
    isAddToBagClicked: isButtonClicked,
    addToBag,
  };
}

export function useAddToWishlist({ selectedSize, hasStock, product, disableNotifyMe }) {
  const { isButtonClicked, setIsButtonClicked, status, setStatus } = useAddProduct({
    selectedSize,
    hasStock,
    disableNotifyMe,
  });

  function addToWishlist() {
    setIsButtonClicked(true);

    if (
      status === STATUS_CODES.ADD_PRODUCT_READY ||
      status === STATUS_CODES.SIZE_OUT_OF_STOCK ||
      status === STATUS_CODES.SIZE_OUT_OF_STOCK_NON_FULL_PRICE
    ) {
      setStatus(STATUS_CODES.ADDING_PRODUCT);

      return ajaxRequest('POST', `${addToWishlistUrl}/${selectedSize.sku}`)
        .then(res => {
          dlAddToWishlist({
            product,
            selectedSize: selectedSize.dataLayerName,
          });
          dlAddToWishlistNew(
            {
              ...product,
              code: selectedSize.sku,
              selectedSize: selectedSize.dataLayerName,
            },
            getListPropsForAddToBagAndAddToWishlist(product.resultPosition)
          );
          setStatus(STATUS_CODES.ADDED_PRODUCT);
          return res;
        })
        .then(json => {
          MINI_WISHLIST_ROOT_ELEMENT.dispatchEvent(
            new CustomEvent(TRANSFER_MINI_WISHLIST_ADD, { detail: { ...json, addedProductId: product.id } })
          );
          return json;
        })
        .catch(res => {
          setStatus(STATUS_CODES.ADD_PRODUCT_ERROR);
          return Promise.reject(res);
        });
    }
    dlAddToWishlist({ product });
    return Promise.resolve();
  }

  return {
    addToWishlistState: status,
    isAddToWishlistClicked: isButtonClicked,
    addToWishlist,
  };
}
